import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { getStoredSession } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import { useCitizenContext } from '@accenturecodefoundry/mfe-pot-citizen-context';
import { useLocale, useTranslations } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type { JobListing } from 'job-bank-data-access';
import { JobListingCard } from '../components/JobListingCard';
import { JobDetailsPage } from './JobDetailsPage';
import { RefinePanel } from '../components/RefinePanel';
import { useJobMatches, type RefineParams } from '../use-job-matches';
import { assetBaseUrl } from '../asset-base-url';
import './JobMatchesPage.css';

type Tab = 'jobs' | 'training' | 'explore';

type View =
  | { tag: 'list' }
  | { tag: 'details'; listing: JobListing };

function SkeletonCard() {
  return (
    <li className="job-matches__skeleton-card" aria-hidden="true">
      <div className="job-matches__skeleton-avatar" />
      <div className="job-matches__skeleton-body">
        <div className="job-matches__skeleton-line job-matches__skeleton-line--title" />
        <div className="job-matches__skeleton-line job-matches__skeleton-line--sub" />
        <div className="job-matches__skeleton-line job-matches__skeleton-line--sub" />
      </div>
    </li>
  );
}

function filterListings(listings: JobListing[], search: string, locale: 'en' | 'fr'): JobListing[] {
  const q = search.trim().toLowerCase();
  if (!q) return listings;
  return listings.filter((l) => {
    const title = (locale === 'fr' ? l.title.fr : l.title.en).toLowerCase();
    const skills = l.skillsChecklist.map((s) => (locale === 'fr' ? s.fr : s.en).toLowerCase());
    // TODO: implement server-side search across full pool
    return title.includes(q) || l.employer.toLowerCase().includes(q) || skills.some((s) => s.includes(q));
  });
}

export function JobMatchesPage() {
  const [activeTab, setActiveTab] = useState<Tab>('jobs');
  const [searchValue, setSearchValue] = useState('');
  const [locationValue, setLocationValue] = useState('');
  const [refinePanelOpen, setRefinePanelOpen] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [lastRefineParams, setLastRefineParams] = useState<RefineParams | null>(null);
  const [view, setView] = useState<View>({ tag: 'list' });
  const refineTriggerRef = useRef<HTMLButtonElement | null>(null);
  const returnFocusIdRef = useRef<string | null>(null);

  const locale = useLocale();
  const { t } = useTranslations(assetBaseUrl, locale);
  const { data, status } = useCitizenContext();
  const sessionName = getStoredSession()?.name;
  const displayName = status === 'ready' ? (data?.name ?? sessionName) : sessionName;
  const { phase, skills = null, retry, goToPage, applyRefine } = useJobMatches();

  useEffect(() => {
    if (status === 'ready') {
      setLocationValue(data?.address?.city ?? '');
    }
  }, [status]);

  // Returning from the detail view unmounts its back button, so focus would
  // fall to <body>. Restore it to the chevron the citizen left from.
  useEffect(() => {
    if (view.tag !== 'list') return;
    const id = returnFocusIdRef.current;
    if (id === null) return;
    returnFocusIdRef.current = null;
    document.getElementById(`job-${id}-chevron`)?.focus();
  }, [view]);

  const headingText = displayName
    ? `${displayName}, ${t('jobMatches.heading.suffix')}`
    : t('jobMatches.heading.suffix');

  const resultLocation = locationValue || t('jobMatches.results.defaultLocation');

  const visibleListings =
    phase.tag === 'ready'
      ? filterListings(phase.listings, searchValue, locale).filter(
          (l) => !dismissedIds.has(l.id),
        )
      : [];

  function renderJobsContent() {
    if (phase.tag === 'loading' || phase.tag === 'loading-listings') {
      return (
        <ul className="job-matches__job-list" aria-label={t('jobMatches.loading')}>
          {Array.from({ length: 5 }, (_, i) => (
            <SkeletonCard key={i} />
          ))}
        </ul>
      );
    }

    if (phase.tag === 'error') {
      return (
        <div className="job-matches__error-state" role="alert">
          <p>{t('jobMatches.error.heading')}</p>
          <button type="button" className="job-matches__retry-btn" onClick={retry}>
            {t('jobMatches.error.retry')}
          </button>
        </div>
      );
    }

    return (
      <>
        {skills !== null && skills.length === 0 && (
          <div className="job-matches__add-skills-prompt">
            <p className="job-matches__empty-heading">{t('jobMatches.empty.heading')}</p>
          </div>
        )}
        <div className="job-matches__results-header">
          <p className="job-matches__results-count">
            {t('jobMatches.results.count', { count: phase.total, location: resultLocation })}
          </p>
          <button
            ref={refineTriggerRef}
            type="button"
            className="job-matches__refine-btn"
            onClick={() => setRefinePanelOpen(true)}
          >
            {t('jobMatches.results.refine')}
          </button>
        </div>

        <ul className="job-matches__job-list">
          {visibleListings.map((listing) => (
            <li key={listing.id}>
              <JobListingCard
                listing={listing}
                onDismiss={(id) => setDismissedIds((prev) => new Set([...prev, id]))}
                onSelect={(listing) => setView({ tag: 'details', listing })}
              />
            </li>
          ))}
        </ul>

        {phase.totalPages > 1 && (
          <nav className="job-matches__pagination" aria-label={t('jobMatches.pagination.label')}>
            <button
              type="button"
              className="job-matches__pagination-btn"
              disabled={phase.page === 1}
              onClick={() => goToPage(phase.page - 1)}
            >
              {t('jobMatches.pagination.previous')}
            </button>
            <span className="job-matches__pagination-indicator">
              {t('jobMatches.pagination.indicator', { page: phase.page, total: phase.totalPages })}
            </span>
            <button
              type="button"
              className="job-matches__pagination-btn"
              disabled={phase.page === phase.totalPages}
              onClick={() => goToPage(phase.page + 1)}
            >
              {t('jobMatches.pagination.next')}
            </button>
          </nav>
        )}
      </>
    );
  }

  if (view.tag === 'details') {
    const listingMatches =
      phase.tag === 'ready' ? (phase.matchedSkills[view.listing.id] ?? []) : [];
    return (
      <div className="job-matches scds-container--narrow">
        <JobDetailsPage
          listing={view.listing}
          matchedSkills={listingMatches}
          onBack={() => {
            returnFocusIdRef.current = view.listing.id;
            setView({ tag: 'list' });
          }}
        />
      </div>
    );
  }

  return (
    <div className="job-matches scds-container--narrow">
      <section className="job-matches__heading">
        <div className="job-matches__heading-title-wrap">
          <h1 className="job-matches__h1">{headingText}</h1>
          <div aria-hidden="true" className="job-matches__accent-bar" />
        </div>
      </section>

      <div className="job-matches__tabs" role="tablist">
        <button
          role="tab"
          id="tab-jobs"
          aria-controls="tabpanel-jobs"
          aria-selected={activeTab === 'jobs'}
          className={`job-matches__tab${activeTab === 'jobs' ? ' job-matches__tab--active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          {t('jobMatches.tabs.jobs')}
        </button>
        <button
          role="tab"
          id="tab-training"
          aria-controls="tabpanel-training"
          aria-selected={activeTab === 'training'}
          className={`job-matches__tab${activeTab === 'training' ? ' job-matches__tab--active' : ''}`}
          onClick={() => setActiveTab('training')}
        >
          {t('jobMatches.tabs.training')}
        </button>
        <button
          role="tab"
          id="tab-explore"
          aria-controls="tabpanel-explore"
          aria-selected={activeTab === 'explore'}
          className={`job-matches__tab${activeTab === 'explore' ? ' job-matches__tab--active' : ''}`}
          onClick={() => setActiveTab('explore')}
        >
          {t('jobMatches.tabs.explore')}
        </button>
      </div>

      {activeTab === 'jobs' && (
        <div
          role="tabpanel"
          id="tabpanel-jobs"
          aria-labelledby="tab-jobs"
          className="job-matches__tabpanel"
        >
          <div className="job-matches__search-row">
            <input
              type="search"
              className="job-matches__search-input"
              placeholder={t('jobMatches.search.placeholder')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.currentTarget.value)}
              aria-label={t('jobMatches.search.label')}
            />
            <input
              type="text"
              className="job-matches__location-input"
              value={locationValue}
              onChange={(e) => setLocationValue(e.currentTarget.value)}
              aria-label={t('jobMatches.search.locationLabel')}
            />
          </div>
          {renderJobsContent()}
        </div>
      )}

      {activeTab === 'training' && (
        <div
          role="tabpanel"
          id="tabpanel-training"
          aria-labelledby="tab-training"
          className="job-matches__tabpanel job-matches__tabpanel--placeholder"
        >
          <p>{t('jobMatches.tabs.trainingPlaceholder')}</p>
        </div>
      )}

      {activeTab === 'explore' && (
        <div
          role="tabpanel"
          id="tabpanel-explore"
          aria-labelledby="tab-explore"
          className="job-matches__tabpanel job-matches__tabpanel--placeholder"
        >
          <p>{t('jobMatches.tabs.explorePlaceholder')}</p>
        </div>
      )}

      {refinePanelOpen && (
        <RefinePanel
          onClose={() => setRefinePanelOpen(false)}
          onApply={(params) => {
            setLastRefineParams(params);
            setDismissedIds(new Set());
            applyRefine(params);
            setRefinePanelOpen(false);
          }}
          initialValues={lastRefineParams ?? undefined}
          triggerRef={refineTriggerRef}
        />
      )}
    </div>
  );
}
